import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, AlertTriangle, XCircle, LogOut, MessageSquare, UserCircle, ShieldOff, Snowflake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function MerchantPendingScreen() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const status = user?.merchant_platform_status;
  const comment = user?.merchant_admin_comment;

  const isNew = status === 'new';
  const isRejected = status === 'rejected';
  const isBlocked = status === 'blocked';
  const isFrozen = status === 'frozen';

  const getIcon = () => {
    if (isBlocked) return <ShieldOff className="mx-auto h-12 w-12 text-destructive" />;
    if (isFrozen) return <Snowflake className="mx-auto h-12 w-12 text-blue-500" />;
    if (isRejected) return <XCircle className="mx-auto h-12 w-12 text-destructive" />;
    return <Clock className="mx-auto h-12 w-12 text-yellow-500" />;
  };

  const getTitle = () => {
    if (isBlocked) return t('misc_pending.t_blocked');
    if (isFrozen) return t('misc_pending.t_frozen');
    if (isRejected) return t('misc_pending.t_rejected');
    return t('misc_pending.t_review');
  };

  const getDescription = () => {
    if (isBlocked) {
      return t('misc_pending.d_blocked');
    }
    if (isFrozen) {
      return t('misc_pending.d_frozen');
    }
    if (isRejected) {
      return t('misc_pending.d_rejected');
    }
    return t('misc_pending.d_review');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center space-y-3">
          {getIcon()}
          <CardTitle className="text-xl">{getTitle()}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {getDescription()}
          </p>

          {comment && (
            <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <MessageSquare size={14} />
                {t('misc_pending.admin_comment')}
              </div>
              <p className="text-sm text-muted-foreground">{comment}</p>
            </div>
          )}

          {isNew && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-yellow-600 mt-0.5 shrink-0" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  {t('misc_pending.new_hint')}
                </p>
              </div>
            </div>
          )}

          {(isBlocked || isFrozen) && (
            <div className={`rounded-lg border p-3 ${isBlocked ? 'border-destructive/30 bg-destructive/5' : 'border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800'}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${isBlocked ? 'text-destructive' : 'text-blue-600'}`} />
                <p className={`text-xs ${isBlocked ? 'text-destructive' : 'text-blue-800 dark:text-blue-200'}`}>
                  {isBlocked
                    ? t('misc_pending.blocked_hint')
                      : t('misc_pending.frozen_hint')}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center pt-2">
            {(isNew || isBlocked || isFrozen) && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/profile">
                  <UserCircle size={14} className="mr-1" />
                  {t('misc_pending.view_profile')}
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="text-destructive">
              <LogOut size={14} className="mr-1" />
              {t('common.logout')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
